import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { getDefaultRoute } from '@/router';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
export default function Login() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [identifier, setIdentifier] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const response = await login(identifier, password);
            const user = response.data.user;
            navigate(getDefaultRoute(user));
        } catch (err) {
            setError(err.response?.data?.message || err.response?.data?.errors?.email?.[0] || 'Error al iniciar sesión.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-black">
            {/* Navbar — same as home */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-black/20">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3">
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-white">
                            <path d="M12 2l8.66 5v10l-8.66 5-8.66-5V7z" />
                            <path d="M12 12m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0" />
                        </svg>
                        <span className="text-sm font-bold uppercase tracking-[0.2em]">GYMS.ar</span>
                    </Link>
                    <div className="flex items-center gap-2 sm:gap-5">
                        <Link to="/register" className="hidden sm:flex items-center text-white/90 hover:text-white transition-colors uppercase text-xs tracking-[0.15em] font-medium">
                            Registrarse
                        </Link>
                    </div>
                </div>
            </nav>

            <div className="min-h-screen flex items-center justify-center px-4 pt-20">
                <Card className="w-full max-w-md bg-[#0a0a0a] border-hairline-dark">
                    <CardHeader className="text-center">
                        <CardTitle className="text-2xl font-bold text-white uppercase tracking-wide">Iniciar Sesión</CardTitle>
                        <CardDescription className="text-white/60">Ingresá tus credenciales para acceder al sistema</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            {error && (
                                <div className="bg-[#0a0a0a] text-white text-sm p-3 rounded-md border border-white/20">
                                    {error}
                                </div>
                            )}
                            <div className="space-y-2">
                                <Label htmlFor="identifier" className="text-white">Email o DNI</Label>
                                <Input
                                    id="identifier"
                                    type="text"
                                    placeholder="tu@email.com o 12345678"
                                    value={identifier}
                                    onChange={(e) => setIdentifier(e.target.value)}
                                    required
                                    className="bg-[#0a0a0a] text-white placeholder:text-white/30 border-hairline-dark"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="password" className="text-white">Contraseña</Label>
                                <Input
                                    id="password"
                                    type="password"
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    className="bg-[#0a0a0a] text-white placeholder:text-white/30 border-hairline-dark"
                                />
                            </div>
                            <Button type="submit" className="w-full" disabled={loading}>
                                {loading ? 'Ingresando...' : 'Ingresar'}
                            </Button>
                        </form>
                        <div className="relative my-6">
                            <Separator className="bg-[#3a3a3f]" />
                            <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#0a0a0a] px-2 text-xs text-white/40">O</span>
                        </div>
                        <a
                            href="/auth/google/redirect"
                            className="flex items-center justify-center gap-3 w-full px-4 py-2.5 rounded-md border border-[#3a3a3f] text-white hover:bg-white/5 transition-colors text-sm font-medium"
                        >
                            <svg className="h-5 w-5" viewBox="0 0 24 24">
                                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                            </svg>
                            Ingresar con Google
                        </a>
                        <div className="mt-4 text-center text-sm">
                            <span className="text-white/60">¿No tenés cuenta? </span>
                            <Link to="/register" className="text-white underline">
                                Registrate
                            </Link>
                        </div>
                        <div className="mt-2 text-center">
                            <Link to="/" className="text-sm text-white/60 hover:text-white underline">
                                ← Volver al inicio
                            </Link>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
